import { buildClientAppPlanFromConsultation } from "./clientAppMapper";
import {
  assertFollowupSafetyPreflightV1,
  buildFollowupSafetySnapshotV1,
} from "./followupSafetySnapshot";
import {
  createFourWeekRotationDraft,
  refreshFourWeekRotationDraft,
  validateFourWeekRotation,
} from "./fourWeekRotation";
import {
  buildModularMealPrepEditorialDraft,
  buildPublishedModularMealPrep,
} from "./mealPrep";
import { mealKey } from "./extractMealsFromPlan";
import { sha256HexSync } from "./sha256Sync";
import {
  formatStructuredIngredient,
  normalizeStructuredIngredients,
  STRUCTURED_INGREDIENT_CATEGORIES,
  STRUCTURED_INGREDIENT_UNITS,
} from "./structuredRecipe";
import { redactDirectIdentifiers } from "./externalPlanWorkflow";

export const CLIENT_APP_PACKAGE_SCHEMA = "anissa_client_app_package";
export const CLIENT_APP_PACKAGE_VERSION = 1;

const TOP_LEVEL_KEYS = new Set([
  "schema",
  "version",
  "source_revision",
  "meals",
  "meal_prep",
]);
const MEAL_KEYS = new Set(["slot", "primary", "alternatives"]);
const ALTERNATIVE_KEYS = new Set(["title", "hint", "recipe"]);
const RECIPE_KEYS = new Set([
  "ingredients_structured",
  "preparation",
  "prep_time_min",
  "cook_time_min",
  "servings",
  "tip",
]);
const MEAL_PREP_KEYS = new Set(["people", "session_count", "preferred_days"]);
const SOURCE_REVISION_RE = /^client-app-package-v1-[a-f0-9]{64}$/;
const TEMPLATE_TEXTS = new Set([
  "aliment exact",
  "etape complete",
  "resume chiffre facultatif",
  "conseil pratique facultatif",
]);
const ALLOWED_DAYS = new Set([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);

function hasExactKeys(value, keys) {
  return value && typeof value === "object" && !Array.isArray(value)
    && Object.keys(value).every((key) => keys.has(key))
    && [...keys].every((key) => Object.hasOwn(value, key));
}

function hasKeys(value, { allowed, required }) {
  return value && typeof value === "object" && !Array.isArray(value)
    && Object.keys(value).every((key) => allowed.has(key))
    && required.every((key) => Object.hasOwn(value, key));
}

function cleanText(value, maxLength, { optional = false } = {}) {
  if (value === undefined && optional) return "";
  if (typeof value !== "string") return null;
  const text = value.replace(/\s+/g, " ").trim();
  if (!text) return optional ? "" : null;
  return text.length <= maxLength ? text : null;
}

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.keys(value).sort().map((key) => [key, stableValue(value[key])]),
  );
}

function normalizeTitle(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr")
    .replace(/\s+/g, " ")
    .trim();
}

function isTemplateText(value) {
  return TEMPLATE_TEXTS.has(normalizeTitle(value));
}

function safeContextValue(value, identity) {
  if (Array.isArray(value)) return value.map((item) => safeContextValue(item, identity));
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [
      key,
      safeContextValue(item, identity),
    ]));
  }
  return typeof value === "string" ? redactDirectIdentifiers(value, identity) : value;
}

function sourceMeals(weekMeals) {
  const meals = Array.isArray(weekMeals?.days?.[0]?.meals)
    ? weekMeals.days[0].meals
    : [];
  if (meals.length === 0 || meals.length > 8) {
    throw new Error("La semaine type doit être complète avant de préparer le dossier application.");
  }
  const slots = new Set();
  return meals.map((meal) => {
    const slot = cleanText(meal?.slot, 80);
    const title = cleanText(meal?.title, 180);
    const label = cleanText(meal?.slot_label || meal?.slot, 80);
    const hint = cleanText(meal?.hint, 300, { optional: true });
    if (!slot || !title || !label || slots.has(slot)) {
      throw new Error("La structure des repas doit être relue avant de préparer le dossier application.");
    }
    slots.add(slot);
    return { slot, slot_label: label, title, ...(hint ? { hint } : {}) };
  });
}

export function buildClientAppPackageContext({ client, consultation, weekMeals, directives = "" }) {
  if (!client?.id || !consultation?.id) {
    throw new Error("La version du programme est indisponible.");
  }
  assertFollowupSafetyPreflightV1(client.form || {});
  const safetySnapshot = buildFollowupSafetySnapshotV1(client.form || {});
  const identity = {
    prenom: client?.prenom,
    nom: client?.nom || client?.form?.nom,
    email: client?.email || client?.form?.email,
  };
  const safeSafetySnapshot = safeContextValue(safetySnapshot, identity);
  const safeDirectives = redactDirectIdentifiers(directives, identity);
  const meals = sourceMeals(weekMeals);
  const source = stableValue({
    version: CLIENT_APP_PACKAGE_VERSION,
    client_id: String(client.id),
    consultation_id: String(consultation.id),
    nutrition_plan: String(consultation?.nutrition_plan || consultation?.nutritionPlan || ""),
    structure: weekMeals?.structure || null,
    meals,
    safety_snapshot: safeSafetySnapshot,
    directives: safeDirectives,
  });
  return Object.freeze({
    sourceRevision: `client-app-package-v1-${sha256HexSync(JSON.stringify(source))}`,
    meals: Object.freeze(meals),
    safetySnapshot: Object.freeze(safeSafetySnapshot),
    directives: safeDirectives,
  });
}

export function buildClientAppPackagePrompt(context) {
  if (!context || !SOURCE_REVISION_RE.test(context.sourceRevision || "")
      || !Array.isArray(context.meals) || context.meals.length === 0) {
    throw new Error("Les instructions du dossier application sont indisponibles.");
  }
  const mealTemplate = context.meals.map((meal) => ({
    slot: meal.slot,
    primary: {
      title: meal.title,
      hint: meal.hint || "Résumé chiffré facultatif",
      recipe: {
        ingredients_structured: [{
          foodKey: "aliment-exact",
          label: "Aliment exact",
          quantity: 100,
          unit: "g",
          preparation: "",
          category: "other",
        }],
        preparation: ["Étape complète"],
        prep_time_min: 10,
        cook_time_min: 10,
        servings: 1,
        tip: "Conseil pratique facultatif",
      },
    },
    alternatives: Array.from({ length: 4 }, (_, index) => ({
      title: `Variante ${index + 1}`,
      hint: "Résumé chiffré facultatif",
      recipe: {
        ingredients_structured: [{
          foodKey: "aliment-exact",
          label: "Aliment exact",
          quantity: 100,
          unit: "g",
          preparation: "",
          category: "other",
        }],
        preparation: ["Étape complète"],
        prep_time_min: 10,
        cook_time_min: 10,
        servings: 1,
        tip: "Conseil pratique facultatif",
      },
    })),
  }));
  const template = {
    schema: CLIENT_APP_PACKAGE_SCHEMA,
    version: CLIENT_APP_PACKAGE_VERSION,
    source_revision: context.sourceRevision,
    meals: mealTemplate,
    meal_prep: {
      people: 1,
      session_count: 2,
      preferred_days: ["sunday", "wednesday"],
    },
  };
  return [
    "DOSSIER APPLICATION — CONTRAT STRICT",
    "",
    "MISSION",
    "Préparer les menus, recettes et réglages logistiques de l’application à partir du programme déjà validé.",
    "Le programme et les contraintes fournis sont l’autorité. Ne les corrige pas, ne les remplace pas et n’ajoute aucune décision clinique.",
    "",
    "RÈGLES IMPÉRATIVES",
    "- Conserver exactement chaque valeur de slot et source_revision.",
    "- Pour chaque repas, produire un menu principal concret avec sa recette et 4 à 5 alternatives réellement différentes.",
    "- Si le titre autoritatif décrit seulement une structure générique, le transformer en vrai menu sans changer la structure nutritionnelle demandée.",
    "- Respecter toutes les allergies, restrictions, aliments évités, statuts et contraintes structurées du dossier.",
    "- Les alternatives doivent rester équivalentes à la structure du programme. Aucun ajout de complément, traitement, dose médicale ou promesse.",
    "- Chaque ingrédient doit avoir un libellé, une quantité positive explicite, une unité autorisée et une catégorie.",
    "- Ne jamais écrire une quantité approximative, une plage, une fraction ambiguë ou une unité dans le champ quantity.",
    "- Utiliser quantity=null uniquement avec unit=to_taste.",
    "- Prévoir des recettes réalistes, simples, compatibles avec le Meal Prep et suffisamment précises pour calculer les courses.",
    "- Répondre uniquement avec l’objet JSON final. Aucun markdown, commentaire ou texte autour.",
    "",
    `UNITÉS AUTORISÉES : ${STRUCTURED_INGREDIENT_UNITS.join(", ")}`,
    `CATÉGORIES AUTORISÉES : ${STRUCTURED_INGREDIENT_CATEGORIES.join(", ")}`,
    "",
    "REPAS AUTORITATIFS",
    JSON.stringify(context.meals, null, 2),
    "",
    "CONTRAINTES STRUCTURÉES RELUES",
    JSON.stringify(context.safetySnapshot, null, 2),
    "",
    "DIRECTIVES COMPLÉMENTAIRES RELUES",
    context.directives || "Aucune directive complémentaire.",
    "",
    "FORMAT EXACT À REMPLIR",
    JSON.stringify(template, null, 2),
    "",
    "CONTRÔLE AVANT RÉPONSE",
    "- Vérifier que tous les slots demandés sont présents exactement une fois.",
    "- Vérifier que chaque recette contient tous ses grammages, toutes ses unités et toutes ses étapes.",
    "- Vérifier qu’aucun aliment interdit ou évité n’apparaît.",
    "- Vérifier que les 4 premières propositions permettent quatre semaines réellement distinctes.",
    "- Fournir uniquement le JSON final.",
  ].join("\n");
}

function parseJsonText(rawText) {
  if (typeof rawText !== "string" || rawText.trim().length < 80) {
    throw new Error("Colle le dossier application complet avant de le vérifier.");
  }
  let text = rawText.trim();
  const fenced = text.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  if (fenced) text = fenced[1].trim();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error("Le contenu collé ne respecte pas le format attendu.");
  }
}

function normalizeRecipe(value, label) {
  if (!hasKeys(value, {
    allowed: RECIPE_KEYS,
    required: ["ingredients_structured", "preparation", "prep_time_min", "cook_time_min", "servings"],
  })) {
    throw new Error(`${label} : structure de recette incomplète.`);
  }
  const ingredientsStructured = normalizeStructuredIngredients(value.ingredients_structured);
  if (!ingredientsStructured || ingredientsStructured.some((ingredient) => isTemplateText(ingredient.label))) {
    throw new Error(`${label} : ingrédients ou grammages incomplets.`);
  }
  if (!Array.isArray(value.preparation) || value.preparation.length < 1 || value.preparation.length > 20) {
    throw new Error(`${label} : étapes de préparation incomplètes.`);
  }
  const preparation = value.preparation.map((step) => cleanText(step, 500));
  if (preparation.some((step) => !step || isTemplateText(step))) {
    throw new Error(`${label} : une étape de préparation est invalide.`);
  }
  const prepTime = value.prep_time_min;
  const cookTime = value.cook_time_min;
  const servings = value.servings;
  if (!Number.isInteger(prepTime) || prepTime < 0 || prepTime > 300
      || !Number.isInteger(cookTime) || cookTime < 0 || cookTime > 600
      || !Number.isInteger(servings) || servings < 1 || servings > 20) {
    throw new Error(`${label} : temps ou nombre de portions invalide.`);
  }
  const tip = cleanText(value.tip, 500, { optional: true });
  if (tip === null || (tip && isTemplateText(tip))) {
    throw new Error(`${label} : conseil trop long ou invalide.`);
  }
  const ingredients = ingredientsStructured.map(formatStructuredIngredient);
  if (ingredients.some((ingredient) => !ingredient)) {
    throw new Error(`${label} : affichage des ingrédients impossible.`);
  }
  return {
    ingredients,
    ingredients_structured: ingredientsStructured,
    preparation,
    prep_time_min: prepTime,
    cook_time_min: cookTime,
    servings,
    ...(tip ? { tip } : {}),
  };
}

function normalizeMealPrep(value) {
  if (!hasExactKeys(value, MEAL_PREP_KEYS)
      || !Number.isInteger(value.people) || value.people < 1 || value.people > 20
      || !Number.isInteger(value.session_count) || value.session_count < 1 || value.session_count > 3
      || !Array.isArray(value.preferred_days)
      || value.preferred_days.length !== value.session_count
      || new Set(value.preferred_days).size !== value.preferred_days.length
      || !value.preferred_days.every((day) => ALLOWED_DAYS.has(day))) {
    throw new Error("Les réglages du Meal Prep sont incomplets ou invalides.");
  }
  return {
    people: value.people,
    session_count: value.session_count,
    preferred_days: [...value.preferred_days],
  };
}

export function parseClientAppPackage(rawText, context) {
  const value = parseJsonText(rawText);
  if (!hasExactKeys(value, TOP_LEVEL_KEYS)
      || value.schema !== CLIENT_APP_PACKAGE_SCHEMA
      || value.version !== CLIENT_APP_PACKAGE_VERSION
      || value.source_revision !== context?.sourceRevision) {
    throw new Error("Ce dossier ne correspond pas à la version actuelle du programme.");
  }
  if (!Array.isArray(value.meals) || value.meals.length !== context.meals.length) {
    throw new Error("Le dossier ne contient pas tous les repas du programme.");
  }
  const expectedBySlot = new Map(context.meals.map((meal) => [meal.slot, meal]));
  const seenSlots = new Set();
  const meals = value.meals.map((mealValue) => {
    if (!hasExactKeys(mealValue, MEAL_KEYS)
        || typeof mealValue.slot !== "string"
        || !expectedBySlot.has(mealValue.slot)
        || seenSlots.has(mealValue.slot)) {
      throw new Error("Un repas du dossier est absent, dupliqué ou inconnu.");
    }
    seenSlots.add(mealValue.slot);
    const sourceMeal = expectedBySlot.get(mealValue.slot);
    if (!hasKeys(mealValue.primary, {
      allowed: ALTERNATIVE_KEYS,
      required: ["title", "recipe"],
    })) {
      throw new Error(`${sourceMeal.slot_label} : menu principal incomplet.`);
    }
    const primaryTitle = cleanText(mealValue.primary.title, 180);
    const primaryHint = cleanText(mealValue.primary.hint, 300, { optional: true });
    if (!primaryTitle || primaryHint === null || (primaryHint && isTemplateText(primaryHint))) {
      throw new Error(`${sourceMeal.slot_label} : menu principal invalide.`);
    }
    const primaryRecipe = normalizeRecipe(
      mealValue.primary.recipe,
      `${sourceMeal.slot_label} · ${primaryTitle}`,
    );
    if (!Array.isArray(mealValue.alternatives)
        || mealValue.alternatives.length < 4
        || mealValue.alternatives.length > 5) {
      throw new Error(`${sourceMeal.slot_label} : 4 à 5 alternatives sont nécessaires.`);
    }
    const titles = new Set([normalizeTitle(primaryTitle)]);
    const alternatives = mealValue.alternatives.map((alternative, index) => {
      if (!hasKeys(alternative, {
        allowed: ALTERNATIVE_KEYS,
        required: ["title", "recipe"],
      })) {
        throw new Error(`${sourceMeal.slot_label} : alternative ${index + 1} incomplète.`);
      }
      const title = cleanText(alternative.title, 180);
      const hint = cleanText(alternative.hint, 300, { optional: true });
      const normalizedTitle = normalizeTitle(title);
      if (!title || /^variante\s+[1-5]$/i.test(title) || hint === null
          || (hint && isTemplateText(hint)) || titles.has(normalizedTitle)) {
        throw new Error(`${sourceMeal.slot_label} : titres d’alternatives invalides ou répétés.`);
      }
      titles.add(normalizedTitle);
      return {
        title,
        ...(hint ? { hint } : {}),
        recipe: normalizeRecipe(alternative.recipe, `${sourceMeal.slot_label} · ${title}`),
      };
    });
    return {
      slot: mealValue.slot,
      slot_label: sourceMeal.slot_label,
      title: primaryTitle,
      ...(primaryHint ? { hint: primaryHint } : {}),
      primaryRecipe,
      alternatives,
    };
  });
  if (seenSlots.size !== expectedBySlot.size) {
    throw new Error("Le dossier ne contient pas tous les repas du programme.");
  }
  return Object.freeze({
    schema: CLIENT_APP_PACKAGE_SCHEMA,
    version: CLIENT_APP_PACKAGE_VERSION,
    source_revision: value.source_revision,
    meals: Object.freeze(meals),
    meal_prep: Object.freeze(normalizeMealPrep(value.meal_prep)),
  });
}

export function prepareClientAppPackageDraft({ client, consultation, weekMeals, packageText, directives = "" }) {
  const context = buildClientAppPackageContext({ client, consultation, weekMeals, directives });
  const parsed = parseClientAppPackage(packageText, context);
  const mealOverrides = parsed.meals.map((meal) => ({
    slot: meal.slot,
    title: meal.title,
    ...(meal.hint ? { hint: meal.hint } : {}),
    alternatives: meal.alternatives.map((alternative) => ({
      title: alternative.title,
      ...(alternative.hint ? { hint: alternative.hint } : {}),
    })),
  }));
  const mealRecipes = { ...(consultation.meal_recipes || {}) };
  for (const meal of parsed.meals) {
    mealRecipes[mealKey(meal.slot, meal.title)] = meal.primaryRecipe;
    for (const alternative of meal.alternatives) {
      mealRecipes[mealKey(meal.slot, alternative.title)] = alternative.recipe;
    }
  }
  const existingOverrides = consultation.editorial_overrides || {};
  let nextConsultation = {
    ...consultation,
    meal_recipes: mealRecipes,
    editorial_overrides: {
      ...existingOverrides,
      meals: mealOverrides,
    },
  };
  const mappedWithRecipes = buildClientAppPlanFromConsultation(client, nextConsultation);
  // Les choix de semaines déjà faits par Anissa survivent à une nouvelle
  // application tant qu'ils restent valides pour les recettes appliquées.
  let rotationDraft;
  try {
    rotationDraft = existingOverrides.four_week_rotation
      ? refreshFourWeekRotationDraft(
        mappedWithRecipes.sections.week_meals,
        existingOverrides.four_week_rotation,
      )
      : createFourWeekRotationDraft(mappedWithRecipes.sections.week_meals);
  } catch {
    rotationDraft = createFourWeekRotationDraft(mappedWithRecipes.sections.week_meals);
  }
  const rotation = validateFourWeekRotation(mappedWithRecipes.sections.week_meals, rotationDraft);
  nextConsultation = {
    ...nextConsultation,
    editorial_overrides: {
      ...nextConsultation.editorial_overrides,
      four_week_rotation: rotation,
    },
  };
  const mappedWithRotation = buildClientAppPlanFromConsultation(client, nextConsultation);
  const mealPrepDraft = buildModularMealPrepEditorialDraft(
    mappedWithRotation.sections.week_meals,
    parsed.meal_prep,
  );
  // Décision de Benoit (17.10) : le Meal Prep est facultatif. S'il ne se
  // calcule pas, le reste s'applique ; il reste en brouillon, non publié.
  const validatedMealPrep = { ...mealPrepDraft.override, status: "validated" };
  const mealPrep = mealPrepDraft.result?.ready
    && buildPublishedModularMealPrep(mappedWithRotation.sections.week_meals, validatedMealPrep)
    ? validatedMealPrep
    : { ...mealPrepDraft.override, status: "draft" };
  const contentSha256 = sha256HexSync(JSON.stringify(stableValue(parsed)));
  nextConsultation = {
    ...nextConsultation,
    editorial_overrides: {
      ...nextConsultation.editorial_overrides,
      meal_prep_v2: mealPrep,
      client_app_package: {
        version: CLIENT_APP_PACKAGE_VERSION,
        status: "draft",
        source_revision: context.sourceRevision,
        content_sha256: contentSha256,
      },
    },
  };
  return Object.freeze({
    sourceRevision: context.sourceRevision,
    contentSha256,
    nextConsultation,
    summary: Object.freeze({
      meals: parsed.meals.length,
      recipes: parsed.meals.reduce((total, meal) => total + 1 + meal.alternatives.length, 0),
      alternatives: parsed.meals.reduce((total, meal) => total + meal.alternatives.length, 0),
      weeks: 4,
      people: parsed.meal_prep.people,
      sessions: parsed.meal_prep.session_count,
      preferredDays: Object.freeze([...parsed.meal_prep.preferred_days]),
    }),
    reviewMeals: Object.freeze(parsed.meals.map((meal) => Object.freeze({
      slot: meal.slot,
      label: meal.slot_label,
      primary: meal.title,
      primaryIngredients: Object.freeze([...meal.primaryRecipe.ingredients]),
      alternatives: Object.freeze(meal.alternatives.map((alternative) => Object.freeze({
        title: alternative.title,
        ingredients: Object.freeze([...alternative.recipe.ingredients]),
      }))),
    }))),
  });
}

export async function persistClientAppPackageDraft({
  draft,
  client,
  consultation,
  weekMeals,
  directives = "",
  confirmationProof,
  save,
}) {
  if (!draft || typeof save !== "function"
      || confirmationProof?.explicitlyConfirmed !== true
      || confirmationProof?.sourceRevision !== draft.sourceRevision
      || confirmationProof?.contentSha256 !== draft.contentSha256) {
    throw new Error("Le dossier application doit être relu et confirmé avant l’enregistrement.");
  }
  const currentContext = buildClientAppPackageContext({ client, consultation, weekMeals, directives });
  if (currentContext.sourceRevision !== draft.sourceRevision) {
    throw new Error("Le programme a changé. Copie de nouvelles instructions avant de recommencer.");
  }
  const acceptedAt = new Date().toISOString();
  const packageProvenance = draft.nextConsultation?.editorial_overrides?.client_app_package;
  if (!packageProvenance || packageProvenance.content_sha256 !== draft.contentSha256) {
    throw new Error("Le contenu vérifié ne correspond plus au dossier à enregistrer.");
  }
  return save({
    ...draft.nextConsultation,
    editorial_overrides: {
      ...draft.nextConsultation.editorial_overrides,
      client_app_package: {
        ...packageProvenance,
        status: "validated",
        accepted_at: acceptedAt,
      },
    },
  });
}
